import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Alert, Modal, Typography, Button, Box } from "@mui/material";
import OtpInput from "react-otp-input";


import logoWhite from "../img/logo_black.png";
import {
  API_LOGIN_URL,
  REGISTER_OR_SAVEUSER_URL,
  SEND_OTP_URL,
  VALIDATE_OTP_URL,
} from "../constants";
import { setUser } from "../store/userSlice";
import "./SignUp.css";

export default function SignUp() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);

  const [isSignUpActive, setIsSignUpActive] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    phone: "",
    email: "",
    password: "",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loginError, setLoginError] = useState("");
  const [otp, setOtp] = useState("");

  const [otpError, setOtpError] = useState("");
  const [timer, setTimer] = useState(60);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [showTimer, setShowTimer] = useState(true);

  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginSuccess, setLoginSuccess] = useState("");

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[0-9]{10}$/;
  const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    let errorMsg = "";

    switch (name) {
      case "email":
        errorMsg = emailRegex.test(value) ? "" : "Invalid email address";
        break;
      case "phone":
        errorMsg = phoneRegex.test(value) ? "" : "Invalid phone number";
        break;
      case "password":
        errorMsg = passwordRegex.test(value)
          ? ""
          : "Password must be at least 8 characters long and include at least one letter and one number";
        break;
      default:
        break;
    }

    setErrors({ ...errors, [name]: errorMsg });
  };

  const handleVerifyOtp = async () => {
    try {
      const response = await fetch(VALIDATE_OTP_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          otp: otp,
          recipientEmail: formData.email,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        await saveUser();
      } else {
        setOtpError(data.message || "Invalid OTP. Please try again.");
      }
    } catch (err) {
      console.error("Error during OTP validation:", err);
      setOtpError("Error during OTP validation.");
    } finally {
      setTimer(60);
      setIsButtonDisabled(false);
    }
  };

  const handleResendOtp = async () => {
    setCanResend(false);
    setShowTimer(true);
    setTimer(60);
    try {
      await sendOtp();
    } catch (error) {
      console.error("Failed to resend OTP:", error);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Check for form errors
    if (
      Object.values(errors).some((errorMsg) => errorMsg !== "") ||
      Object.values(formData).some((field) => field === "")
    ) {
      setError("Please fix the errors in the form.");
      return;
    }

    try {
      await sendOtp();
    } catch (err) {
      console.error("Signup error:", err);
      setError("Error during sign up.");
    }
  };

  const sendOtp = async () => {
    try {
      const response = await fetch(SEND_OTP_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          otp: "string",
          recipientEmail: formData.email,
        }),
      });

      if (response.ok) {
        //then only open otp modal
        setOtpModalOpen(true);
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to send OTP");
      }
    } catch (err) {
      console.error("Failed to send OTP:", err);
      setError("Failed to send OTP.");
    }
  };

  const saveUser = async () => {
    try {
      const response = await fetch(REGISTER_OR_SAVEUSER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const user = await response.json();
      // Check if the response status is 200-299
      if (response.ok) {
        setSuccess("Successfully Registered");
        setOtpModalOpen(false);
        setUser(user.data);
        navigate("/");
      } else {
        setError("Sign-up failed. Please try again.");
        setOtpError("Failed to save user data. Please try again.");
        setOtpModalOpen(false);
      }
    } catch (err) {
      console.error("Signup error:", err);
      setError("Error during sign-up.");
    }
  };

  const handleSignUpClick = () => {
    setIsSignUpActive(true);
  };

  const handleSignInClick = () => {
    setIsSignUpActive(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginSuccess("");
    if (!emailRegex.test(loginEmail)) {
      setLoginError("Invalid email address");
      return;
    }

    try {
      const response = await fetch(`${API_LOGIN_URL}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });

      const data = await response.json();
      if (data.status === "success") {
         console.log("in login response ",data);
        setLoginSuccess("Login successful");
        dispatch(setUser(data.data));
      } else {
        setLoginError(data.message || "Login failed. Please try again.");
      }
    } catch (error) {
      console.error("Error during login:", error);
      setLoginError("Error during login.");
    }
  };

  useEffect(() => {
    if (loginError) {
      const timer = setTimeout(() => {
        setLoginError("");
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [loginError]);

  useEffect(() => {
    if (loginSuccess) {
      const timer = setTimeout(() => {
        setLoginSuccess("");
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [loginSuccess]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError("");
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess("");
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    let sId;
    if (user?.id) {
       sId = setTimeout(() => {
        navigate("/");
      }, 500);
    }
    return () => clearTimeout(sId);
  }, [user]);

  useEffect(() => {
    let interval;
    if (otpModalOpen && timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
      setShowTimer(false);
    }
    return () => clearInterval(interval);
  }, [otpModalOpen, timer]);

  useEffect(()=>{
    if(otpError){
      const timer = setTimeout(() => {
        setOtpError("");
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [otpError]);

  return (
    <div className={`container ${isSignUpActive ? "right-panel-active" : ""}`}>
      {success && (
        <Alert
          severity="success"
          style={{ position: "fixed", top: "10px", right: "10px", zIndex: 99 }}
        >
          {success}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          style={{ position: "fixed", top: "10px", right: "10px", zIndex: 99 }}
        >
          {error}
        </Alert>
      )}
      {loginError && (
        <Alert
          severity="error"
          style={{ position: "fixed", top: "10px", right: "10px", zIndex: 99 }}
        >
          {loginError}
        </Alert>
      )}
      {loginSuccess && (
        <Alert
          severity="success"
          style={{ position: "fixed", top: "10px", right: "10px", zIndex: 99 }}
        >
          {loginSuccess}
        </Alert>
      )}
      <div className="form-container sign-up-container">
        <form onSubmit={handleSignUp}>
          <Typography variant="p" sx={{ fontSize: 28, fontWeight: 500 }}>
            Create Account
          </Typography>
          {errors.phone && (
            <Alert severity="error" style={{ width: "100%" }}>
              {errors.phone}
            </Alert>
          )}
          {errors.email && (
            <Alert severity="error" style={{ width: "100%" }}>
              {errors.email}
            </Alert>
          )}
          {errors.password && (
            <Alert severity="error" style={{ width: "100%" }}>
              {errors.password}
            </Alert>
          )}
          <input
            type="text"
            name="firstName"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
          <input
            type="text"
            name="lastName"
            placeholder="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
          <input
            type="text"
            name="phone"
            placeholder="Phone"
            value={formData.phone}
            onChange={handleChange}
            required
          />
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <input
            type="password"
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            required
          />
          <button type="submit">Sign Up</button>
          <div className="small-screen"> 
             <p
                style={{
                    margin:"10px 0",
                }}
             >Already Registered ? Click Below Button</p> 
             <button onClick={handleSignInClick}>
                Sign In
            </button> 
          </div>
        </form>
      </div>
      <div className="form-container sign-in-container">
        <form onSubmit={handleLogin}>
          <Typography variant="p" sx={{ fontSize: 28, fontWeight: 500 }}>
            Sign in
          </Typography>
          <input
            type="email"
            placeholder="Email"
            required
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
          />
          {/* {loginError && <Alert severity="error" style={{ width: '100%' }}>{loginError}</Alert>} */}
          <input
            type="password"
            placeholder="Password"
            required
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
          />
          <a
            href="https://google.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Forgot your password?
          </a>
          <button type="submit">Sign In</button>

          <div className="small-screen"> 
             <p
                style={{
                    margin:"10px 0",
                }}
             >Not Registered ? Click Below Button</p> 
             <button onClick={handleSignUpClick}>
                Sign Up
            </button> 
          </div>
        </form>
      </div>
      <div className="overlay-container">
        <div className="overlay">
          <div className="overlay-panel overlay-left">
            <img src={logoWhite} className="logo" alt="Logo" />
            <Typography variant="p" sx={{ fontSize: 28, fontWeight: 500 }}>
              Welcome Back
            </Typography>
            <p>
              To keep connected with us please login with your personal info
            </p>
            <button className="ghost" onClick={handleSignInClick}>
              Sign In
            </button>
          </div>
          <div className="overlay-panel overlay-right">
            <img src={logoWhite} className="logo" alt="Logo" />
            <Typography variant="p" sx={{ fontSize: 28, fontWeight: 500 }}>
              Hello, Friend!
            </Typography>
            <p>Enter your personal details and start your journey with us</p>
            <button className="ghost" onClick={handleSignUpClick}>
              Sign Up
            </button>
          </div>
        </div>
      </div>

      {/* OTP Modal */}
      <Modal
        open={otpModalOpen}
        onClose={() => {
          setOtpModalOpen(false);
          setOtp("");
          setOtpError("");
          setTimer(60);
          setIsButtonDisabled(false);
          setCanResend(false);
          setShowTimer(true);
        }}
        aria-labelledby="Otp Modal"
        aria-describedby="Modal for OTP verification"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            maxWidth: "100%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "15px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography id="otp-modal" variant="h6" component="h2">
            Enter OTP
          </Typography>
          <OtpInput
            value={otp}
            onChange={setOtp}
            numInputs={6}
            renderSeparator={<span style={{ width: "0px" }}></span>}
            renderInput={(props) => <input {...props} />}
            containerStyle={{ justifyContent: "center", marginTop: "20px" }}
            inputStyle={{
              width: "40px",
              height: "40px",
              margin: "0 4px",
              fontSize: "16px",
              borderRadius: "4px",
              border: "1px solid #ccc",
            }}
          />
          {showTimer && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Time remaining: {timer} seconds
            </Typography>
          )}
          <Button
            onClick={handleVerifyOtp}
            disabled={isButtonDisabled}
            sx={{ mt: 2 }}
          >
            Verify OTP
          </Button>
          <Button
            onClick={handleResendOtp}
            disabled={!canResend}
            sx={{ mt: 2 }}
          >
            Resend OTP
          </Button>
          {otpError && (
            <Typography color="error" sx={{ mt: 2 }}>
              {otpError}
            </Typography>
          )}
        </Box>
      </Modal>
    </div>
  );
}
